// AuthContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Hub } from "aws-amplify/utils";
import {
  getCurrentUser,
  fetchAuthSession,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { initializeNewUserInDB } from "./dynamoUtils";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [showCongrats, setShowCongrats] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // 🔁 Shared handler for authenticated Cognito user
  const handleAuthenticatedUser = useCallback(
    async ({ redirectAfterLogin = false } = {}) => {
      try {
        const cognitoUser = await getCurrentUser();
        const session = await fetchAuthSession();

        const idPayload = session?.tokens?.idToken?.payload;
        const accessPayload = session?.tokens?.accessToken?.payload;

        // Email extraction (unchanged)
        const email =
          idPayload?.email ||
          accessPayload?.username ||
          "";

        // Canonical Cognito username (likely lowercased in your pool)
        const username = cognitoUser.username;

        // 👇 NEW: get custom displayName attribute (pretty casing)
        // fallback to canonical username for old users
        const displayName =
          idPayload?.["custom:displayName"] || username;

        setIsAuthenticated(true);
        setUser({ username, displayName, email });

        // AWS credentials for Dynamo
        const awsCredentials = session?.credentials;

        const today = new Date().toLocaleDateString("en-CA");

        // 👇 UPDATED: pass displayName into Dynamo init
        const result = await initializeNewUserInDB(
          username,
          email,
          today,
          awsCredentials,
          { displayName }      // NEW
        );

        if (redirectAfterLogin) {
          if (result.status === "new") {
            setShowCongrats(true);
            navigate(`/progress/${username}`, { replace: true });
          } else if (result.status === "existing") {
            navigate("/progress", { replace: true });
          } else if (result.status === "error") {
            console.error(
              "User initialization failed; staying on current page.",
              result.error
            );
          }
        }
      } catch (error) {
        console.log("User is not authenticated", error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // 🔄 On initial mount
  useEffect(() => {
    handleAuthenticatedUser({ redirectAfterLogin: false }).catch(() => {
      setIsLoading(false);
    });
  }, [handleAuthenticatedUser]);

  // 📡 Auth event listener
  const authListener = useCallback(
    async ({ payload }) => {
      console.log("Auth event:", payload.event);

      if (payload.event === "signedIn" || payload.event === "signIn") {
        setIsLoading(true);
        await handleAuthenticatedUser({ redirectAfterLogin: true });
      }

      if (payload.event === "signedOut" || payload.event === "signOut") {
        console.log("🚪 User signed out, resetting auth state...");
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
      }
    },
    [handleAuthenticatedUser]
  );

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", authListener);
    return () => unsubscribe();
  }, [authListener]);

  const signOut = async () => {
    try {
      await amplifySignOut();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        signOut,
        showCongrats,
        setShowCongrats,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
